import { useCallback, useEffect, useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import PickXiChrome from "../components/pickxi/PickXiChrome";
import LoadFailedCard from "../components/pickxi/LoadFailedCard";
import GamesEmpty from "../components/pickxi/GamesEmpty";
import PickTheXI from "../components/pickxi/PickTheXI";
import OnTheDay from "../components/pickxi/OnTheDay";
import GamePlanNotes from "../components/pickxi/GamePlanNotes";
import { GamesColumn } from "./CoachGames";
import { usePageWidth } from "../context/PageWidthContext";
import useLaptop from "../lib/useLaptop";
import useCoachGame from "../hooks/useCoachGame";
import { listGames, nextGameId, opensOnTheDay } from "../lib/coachGames";

/**
 * Issue #1316 (Pick XI coach 1 of 3; D309) — `/coach-portal/team`, Team
 * selection. It opens on the next game's team sheet, and the list of games
 * sits one tap further. With no `?game=`, this loads the coach's games:
 * none shows the board's `games-empty` state, otherwise it replaces to
 * `?game=<soonest>`. Loading, failed and empty are separate states (D53).
 *
 * With `?game=<id>` (#1317) it is Pick the XI, step 1 of 2, and with
 * `?game=<id>&step=plan` (#1318) it is Game plan & notes, step 2 of 2. The
 * entry in `CoachSquad` (Team selection) opens this when `pick-xi` is on.
 *
 * D316 Q3 (#745): a PUBLISHED game opens On the day from its date, on the
 * coach's own local date (the `X-Client-Date` rule), and Pick the XI before it;
 * an unpublished game never opens On the day. The page reads the game once to
 * choose, and hands that read to the screen it opens.
 *
 * #1430 (D386): from the laptop tier a game's team sheet (Pick the XI, On the day, Game plan & notes) opens
 * beside Your games, the list at 380px with the open game marked; below it the sheet is the whole page.
 */
export default function CoachTeam() {
  const [params] = useSearchParams();
  const gameId = params.get("game");
  const step = params.get("step");
  const laptop = useLaptop();
  usePageWidth(laptop && gameId ? "wide" : "narrow");
  const besideGames = (sheet) =>
    laptop ? (
      <div className="nw-split">
        <GamesColumn selectedId={gameId} />
        <div data-nw-detail="">{sheet}</div>
      </div>
    ) : (
      sheet
    );
  const [state, setState] = useState({ status: "loading", games: [] });
  // #745: the game is read once, here, to choose between Pick the XI and On the day, and the same
  // read goes to the screen it opens. Not read for the list (no `?game=`) or for the plan step.
  const sheetGameId = gameId && step !== "plan" ? gameId : null;
  const source = useCoachGame(sheetGameId, { skip: !sheetGameId });

  const load = useCallback(() => {
    let live = true;
    setState({ status: "loading", games: [] });
    listGames()
      .then((games) => live && setState({ status: "ready", games }))
      .catch((e) => {
        console.error("CoachTeam: failed to load the games", e);
        if (live) setState({ status: "failed", games: [] });
      });
    return () => {
      live = false;
    };
  }, []);

  useEffect(() => {
    if (gameId) return undefined;
    return load();
  }, [gameId, load]);

  if (gameId && step === "plan") {
    return (
      <PickXiChrome title="Game plan & notes" testId="plan-page">
        {besideGames(<GamePlanNotes key={gameId} gameId={gameId} />)}
      </PickXiChrome>
    );
  }
  // #1317 (D309): `?game=<id>` is Pick the XI, step 1 of 2, or On the day (#745, D316 Q3). While the
  // game loads, and if it fails, the date is not known, so those two states are Pick the XI's own.
  if (gameId) {
    if (source.status === "ready" && opensOnTheDay(source.game)) {
      return (
        <PickXiChrome title="On the day" testId="xi-day-page">
          {besideGames(<OnTheDay key={gameId} gameId={gameId} source={source} besideList={laptop} />)}
        </PickXiChrome>
      );
    }
    return (
      <PickXiChrome title="Pick the XI" testId="xi-page">
        {besideGames(<PickTheXI key={gameId} gameId={gameId} source={source} besideList={laptop} />)}
      </PickXiChrome>
    );
  }

  if (state.status === "ready" && state.games.length > 0) {
    return <Navigate to={`/coach-portal/team?game=${encodeURIComponent(nextGameId(state.games))}`} replace />;
  }

  let body;
  if (state.status === "failed") {
    body = <LoadFailedCard title="Couldn't load your games" testId="team-failed" retryTestId="team-failed-retry" onRetry={load} />;
  } else if (state.status === "ready") {
    body = <GamesEmpty />;
  } else {
    body = (
      <div data-testid="team-loading" aria-busy="true" className="flex flex-col" style={{ gap: 8 }}>
        {[0, 1].map((i) => (
          <div key={i} style={{ height: 76, background: "var(--nw-surface)", border: "1px solid var(--nw-line)", borderRadius: "var(--nw-radius-card)" }} />
        ))}
      </div>
    );
  }

  return (
    <PickXiChrome title="Team selection" testId="team-page">
      <div className="flex flex-col" style={{ gap: 14, padding: "var(--nw-space-page)" }}>
        <div className="nw-eyebrow">Team selection</div>
        {body}
      </div>
    </PickXiChrome>
  );
}
